import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import { Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import type React from "react";
import { useTranslation } from "react-i18next";
import type { AccountRequest } from "../model";

type AccountRequestTableData = AccountRequest;

export type AccountRequestTableProps = {
  accountRequests: AccountRequestTableData[];
  onAccountRequestClick: (accountRequest: AccountRequestTableData) => void;
};

export const AccountRequestTable: React.FC<AccountRequestTableProps> = (
  props,
) => {
  const { t } = useTranslation();
  const data: AccountRequestTableData[] = props.accountRequests;
  const columns: Column<AccountRequestTableData>[] = [
    {
      key: "id",
      label: "ID",
      sortable: true,
      filterable: false,
      cell: (row) => {
        const id = row.id;
        return (
          <div>
            <Typography color={grey[900]}>{id}</Typography>
          </div>
        );
      },
    },
    {
      key: "companyCategory",
      label: `${t("accountRequest.companyCategory")}`,
      sortable: true,
      filterable: true,
      cell: (row) => {
        const companyCategory = row.companyCategory;
        if (!Array.isArray(companyCategory)) {
          console.error(`companyCategory is not an array ${companyCategory}`);
          return null;
        }
        return (
          <Stack direction="row" spacing={1}>
            {companyCategory.map((category) => {
              return (
                <Typography key={category} color={grey[900]}>
                  {t(category)}
                </Typography>
              );
            })}
          </Stack>
        );
      },
    },
    {
      key: "companyName",
      label: `${t("accountRequest.companyName")}/${t("accountRequest.officeName")}`,
      sortable: true,
      filterable: true,
      cell: (row) => {
        const companyName = row.companyName;
        const officeName = row.officeName;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography fontWeight={700} fontSize={18}>
              {companyName}
            </Typography>
            <Typography fontSize={16}>{officeName}</Typography>
          </Stack>
        );
      },
    },
    {
      key: "lastName",
      label: `${t("accountRequest.lastName")}/${t("accountRequest.firstName")}`,
      cell: (row) => {
        const firstName = row.firstName;
        const lastName = row.lastName;
        return (
          <Stack direction="row" spacing={1}>
            <Typography fontSize={16}>{lastName}</Typography>
            <Typography fontSize={16}>{firstName}</Typography>
          </Stack>
        );
      },
    },
    {
      key: "phoneNumber",
      label: `${t("accountRequest.phoneNumber")}`,
      cell: (row) => {
        const phoneNumber = row.phoneNumber;
        return <Typography fontSize={16}>{phoneNumber}</Typography>;
      },
    },
    {
      key: "email",
      label: `${t("accountRequest.email")}`,
      cell: (row) => {
        const email = row.email;
        return <Typography fontSize={16}>{email}</Typography>;
      },
    },
  ];

  const onRowClick = (row: AccountRequestTableData) => {
    props.onAccountRequestClick(row);
  };

  return (
    <BaseTable
      rows={data}
      columns={columns}
      noDataMessage="法人アカウント登録の申請はありません。"
      onRowClick={onRowClick}
    />
  );
};
